import React, { FC, useCallback, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import { adminApi } from 'util/adminApi';
import { ui } from 'util/ui';
import { SettingsCard, SaveRow } from './SettingsCard';

/** Status of the built-in search file, with a button to rebuild it now. Same box as the other settings. */
const SearchSettings: FC = () => {
  const [meta, setMeta] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>('');

  const load = useCallback(async () => {
    const snap = await firebase.firestore().doc('_search/meta').get();
    setMeta(snap.exists ? snap.data() : null);
  }, []);

  useEffect(() => {
    load().catch((e) => setMessage(e.message));
  }, [load]);

  const rebuild = async () => {
    setBusy(true);
    setMessage('');
    try {
      const res = await adminApi.rebuildSearch();
      setMessage(`Rebuilt: ${(res.gzipBytes / 1024).toFixed(0)} KB download.`);
      await load();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const builtAt = meta?.builtAt?.toDate ? meta.builtAt.toDate() : null;
  const pending = meta?.dirtyAt && meta?.builtFromAt && meta.dirtyAt.toMillis() > meta.builtFromAt.toMillis();

  return (
    <SettingsCard
      id="search-h"
      title="Search"
      note="Built-in search. The server rebuilds the search file within 10 minutes of any change; nothing is sent to a third party."
    >
      {meta ? (
        <div style={{ fontSize: 14, color: ui.ink }}>
          <div>
            Last built {builtAt ? builtAt.toLocaleString('en-AU') : '…'}
            <span style={{ color: pending ? '#7A5300' : '#0B6B3F', fontWeight: 600 }}>
              {pending ? ' · changes waiting for the next rebuild' : ' · up to date'}
            </span>
          </div>
          {meta.counts && (
            <div style={{ marginTop: 4, fontSize: 13, color: ui.muted }}>
              {meta.counts.projects} projects · {meta.counts.records} records · {meta.counts.videos} videos · {meta.counts.notes} notes ·{' '}
              {meta.counts.assets} assets · {(meta.gzipBytes / 1024).toFixed(0)} KB
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: ui.muted }}>The search file hasn't been built yet.</div>
      )}
      <SaveRow onSave={rebuild} busy={busy} message={message} label="Rebuild now" busyLabel="Rebuilding…" />
    </SettingsCard>
  );
};

export default SearchSettings;
